import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetTaxCollected,
  type TaxCollectedEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type TaxData = TaxCollectedEntry;

interface TaxesViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const TaxesView = ({
  locationId,
  dateRange,
  tableId,
}: TaxesViewProps) => {
  const data =
    useQuery(locationSalesGetTaxCollected, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isLoading = data === undefined;

  const columnHelper = createColumnHelper<TaxData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("location", {
          header: "Location Name",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.location.id),
          meta: {
            faceting: {
              title: "Location",
              getOption: (r: TaxData["location"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: TaxData["location"]) => r.name,
          },
        }),
        columnHelper.accessor("taxCategory", {
          header: "Tax Category",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.taxCategory.id),
          meta: {
            faceting: {
              title: "Tax Category",
              getOption: (r: TaxData["taxCategory"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: TaxData["taxCategory"]) => r.name,
          },
        }),
        columnHelper.accessor("taxCategory.taxCode", {
          header: "Tax Code",
        }),
        columnHelper.accessor("taxCategory.value", {
          header: "Tax %",
          cell: ({ getValue }) => `${getValue()}%`,
        }),
        columnHelper.accessor("amountSold", {
          header: "Taxable Goods Sales",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TaxData["amountSold"]) => d,
          },
        }),
        columnHelper.accessor("itemsSold", {
          header: "Total Items Ordered",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: TaxData["itemsSold"]) => d,
          },
        }),
        columnHelper.accessor("taxCollected", {
          header: "Tax Collected",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TaxData["taxCollected"]) => d,
          },
        }),
      ] as ColumnDef<TaxData>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading tax data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
